import Loading from "@/common/components/ui/loading/Loading";
import GenericTable from "@/common/components/ui/table/GenericTable";
import {
  TableFilters,
  TableFiltersState,
} from "@/common/components/ui/table/TableFilters";
import TableFooter from "@/common/components/ui/table/TableFooter";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { formatDateAndTime } from "@/common/helpers/date.helpers";
import { formatCurrency } from "@/common/helpers/mask.helpers";
import { getCompanyId } from "@/common/services/company.api";
import { getRentersNegativation } from "@/common/services/renter-negativation.api";
import { useCompanyStore } from "@/common/states/store/Company";
import {
  IRentersNegativation,
  NEGATIVATION_STATUS,
} from "@/types/renters-negativation.type";
import { Suspense, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModalCancelNegativation from "../modals/ModalCancelNegativation";
import { getCompanySigla } from "@/common/helpers/company.helpers";

const STATUS_LABELS = {
  [NEGATIVATION_STATUS.PENDING]: "Pendente",
  [NEGATIVATION_STATUS.AWAITING_APPROVAL]: "Aguardando Aprovação",
  [NEGATIVATION_STATUS.APPROVED]: "Aprovado",
  [NEGATIVATION_STATUS.REJECTED]: "Rejeitado",
  [NEGATIVATION_STATUS.CANCELLED]: "Cancelado",
  [NEGATIVATION_STATUS.PROCESSED]: "Processado",
  [NEGATIVATION_STATUS.UNDEFINED]: "Indefinido",
  [NEGATIVATION_STATUS.DUNNING_REQUESTED]: "Em negativação",
};

const STATUS_COLORS = {
  [NEGATIVATION_STATUS.PENDING]: "bg-bluePrimary text-white",
  [NEGATIVATION_STATUS.AWAITING_APPROVAL]: "bg-yellowPrimary",
  [NEGATIVATION_STATUS.APPROVED]: "bg-greenPrimary text-white",
  [NEGATIVATION_STATUS.REJECTED]: "bg-yellowPrimary",
  [NEGATIVATION_STATUS.CANCELLED]: "bg-redPrimary text-white",
  [NEGATIVATION_STATUS.PROCESSED]: "bg-grayPrimary",
  [NEGATIVATION_STATUS.UNDEFINED]: "bg-grayPrimary",
  [NEGATIVATION_STATUS.DUNNING_REQUESTED]: "bg-orange-500 text-white",
};

function getStatusLabels(status: NEGATIVATION_STATUS) {
  return (
    <span
      className={`inline-block px-2 py-1 rounded-md text-sm whitespace-nowrap overflow-hidden text-ellipsis max-w-[120px] md:max-w-full ${STATUS_COLORS[status]}`}
      title={STATUS_LABELS[status] || status}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

const EMPTY_ACTION = { eventName: "", data: undefined };

const headers = {
  companyId: "Cidade",
  renterName: "Nome do Locatário",
  value: "Valor",
  invoiceNumber: "Número da Fatura",
  status: "Status",
  createdAt: "Criado em",
  dunningNumber: "Número de Cobrança",
};

function TableNegativations() {
  const navigate = useNavigate();
  const company = useCompanyStore();
  const companyId = getCompanyId();
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [negativation, setNegativation] = useState<IRentersNegativation[]>([]);

  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-createdAt",
  });

  useEffect(() => {
    async function loadNegativation(filters, page) {
      setIsLoading(true);
      setNegativation([]);

      const newFilter = {
        ...filters,
        orderBy: "-createdAt",
      };

      const result = await getRentersNegativation({
        ...newFilter,
        page,
      });

      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems,
      });
      setNegativation(result.data);
      setIsLoading(false);
    }

    loadNegativation(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, notifyError, company, companyId]);

  const actions = [
    {
      label: "Cancelar Negativação",
      action: (data) => setAction({ eventName: "cancelNegativation", data }),
      permission: "edit:negativations",
    },
  ];

  const isAction = (eventName: string) => action.eventName === eventName;

  const handleSearch = ({ search, status }) => {
    const newState = Object.assign({}, filters, { search, status });
    setFilters(newState);
  };
  const cleanActionData = () => {
    setAction(EMPTY_ACTION);
    handleSearch(filters);
  };
  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };
  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Cobranças Negativadas"
          placeholder="Digite o nome, número da fatura, número da cobrança"
          statusOptions={STATUS_LABELS}
          onSearch={handleFilterChange}
          entity="negativation"
          tableData={negativation as unknown as Record<string, unknown>[]}
        />

        <GenericTable
          headers={headers}
          data={negativation as unknown as Record<string, unknown>[]}
          actions={actions}
          setFilters={({ orderBy }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              orderBy,
            }));
          }}
          renders={
            {
              companyId: (companyId) => {
                return getCompanySigla(companyId);
              },
              renterName: (name, renter) => (
                <a
                  href={`/clientes/${(renter as IRentersNegativation).renterId
                    }`}
                  onClick={(e) => {
                    e.preventDefault(); // Para evitar o comportamento padrão do link
                    navigate(
                      `/clientes/${(renter as IRentersNegativation).renterId}`
                    ); // Navegação na mesma página
                  }}
                  target="3_blank" // Abre em uma nova aba
                  rel="noopener noreferrer" // Melhoria de segurança
                >
                  {name as string}
                </a>
              ),
              dunningNumber: (dunningNumber, negativation) => (
                <a
                  href={`/negativacao/${(negativation as IRentersNegativation)._id
                    }`}
                  onClick={(e) => {
                    e.preventDefault(); // Para evitar o comportamento padrão do link
                    navigate(
                      `/negativacao/${(negativation as IRentersNegativation)._id
                      }`
                    ); // Navegação na mesma página
                  }}
                  target="_blank" // Abre em uma nova aba
                  rel="noopener noreferrer" // Melhoria de segurança
                >
                  {dunningNumber as string}
                </a>
              ),
              value: formatCurrency,
              invoiceNumber: (invoiceNumber) => (
                <a
                  href={`https://asaas.com/payment/show/${invoiceNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline hover:text-bluePrimary"
                >
                  {invoiceNumber as string}
                </a>
              ),
              createdAt: formatDateAndTime,
              status: getStatusLabels,
              leadOrigin: (leadOrigin) => leadOrigin || "-",
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          }
          entity="renters"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />

        {isAction("cancelNegativation") && (
          <ModalCancelNegativation
            negativationId={(action.data as { _id: string })?._id ?? ""}
            onClose={cleanActionData}
          />
        )}
      </div>
    </Suspense>
  );
}
export default TableNegativations;
